import { type StylableProps, ActionText, Icon, Row, Text } from "@mdotm/shadcn/legacy/components";
import { type Atom, atomWithCompare, useAtomState, WithAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import type { DataAttributesProps } from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { useMemo, useState } from "react";

/**
 * `signal` aborts when the queue is told to abandon what it is holding — see {@link TaskQueue.drain}.
 * Take it into whatever the task sends, and the abandonment reaches the wire — an aborted request
 * rejects, which is what lets the queue move on. A task that ignores its signal instead keeps the
 * queue on it until it settles of its own accord, and everything behind it waits.
 */
export type Task<T> = (signal: AbortSignal) => MaybePromise<T>;

export type TaskQueue = {
	enqueue<T>(task: Task<T>): Promise<T>;
	/**
	 * Resolve once every task enqueued before this call has settled. Never rejects: it reports quiet,
	 * not success — each task's own `enqueue` caller already has its outcome.
	 *
	 * Aborting `signal` abandons the rest instead of waiting for it: the task running now is told
	 * through the signal it was handed, and everything still queued is dropped unrun. What no abort can
	 * do is recall a request a task has already put on the wire.
	 */
	drain(signal?: AbortSignal): Promise<void>;
	state(): TaskQueueState;
};

export type TaskQueueState = {
	isRunning: boolean;
	lastRunAt: {
		timestamp: Date;
		isSuccess: boolean;
	} | null;
};

export function makeTaskQueue(size = 10000, opts?: { onStateChange?(state: TaskQueueState): void }): TaskQueue {
	const queue: Array<{ task: Task<any>; resolve(v: any): void; reject(err?: unknown): void }> = [];

	/** The task running right now, so `drain` has something to abort. */
	let running: AbortController | null = null;
	let state: TaskQueueState = { isRunning: false, lastRunAt: null };
	const setState = (v: TaskQueueState) => {
		state = v;
		opts?.onStateChange?.(v);
	};
	async function run() {
		if (state.isRunning) {
			return;
		}
		setState({ ...state, isRunning: true });
		/** The outcome to report, or null while there is nothing worth reporting. */
		let outcome: boolean | null = null;
		while (queue.length > 0) {
			const cur = queue.shift()!;
			const controller = new AbortController();
			running = controller;
			try {
				const result = await cur.task(controller.signal);
				cur.resolve(result);
				outcome = true;
			} catch (err) {
				cur.reject(err);
				// An abandoned task is neither a failure nor news. Reporting it would put the indicator into its
				// error state, which is the state a consumer offers its Retry from — so a write given up on at a
				// context boundary would come back under whatever replaced the context it was given up for.
				if (!controller.signal.aborted) {
					outcome = false;
				}
			} finally {
				running = null;
			}
		}
		setState({
			...state,
			isRunning: false,
			lastRunAt: outcome === null ? state.lastRunAt : { isSuccess: outcome, timestamp: new Date() },
		});
	}

	const taskQueue: TaskQueue = {
		enqueue(task) {
			return new Promise((resolve, reject) => {
				while (queue.length >= size) {
					// drop the first item
					queue.shift();
				}
				queue.push({
					task,
					resolve,
					reject,
				});
				run().catch(noop);
			});
		},
		drain(signal?: AbortSignal): Promise<void> {
			// The marker goes in first, so it is behind everything this call is waiting for and in front of
			// nothing. Dropping the queue drops it too, and its rejection is what ends the wait early
			// instead of leaving it to outlive the tasks it was abandoning.
			const quiet = taskQueue.enqueue(noop).then(noop, noop);
			if (!signal) {
				return quiet;
			}
			const abandon = () => {
				running?.abort(signal.reason);
				for (const dropped of queue.splice(0)) {
					dropped.reject(signal.reason);
				}
			};
			// A signal that has already aborted dispatches no event, so it is asked directly.
			if (signal.aborted) {
				abandon();
			} else {
				signal.addEventListener("abort", abandon, { once: true });
			}
			return quiet.finally(() => signal.removeEventListener("abort", abandon));
		},
		state() {
			return state;
		},
	};
	return taskQueue;
}

/**
 * Create a simple task queue that can run sync and async functions sequentially.
 */
export function useTaskQueue(size?: number): { queue: TaskQueue; state: TaskQueueState } {
	const [state, setState] = useState<TaskQueueState>(() => ({
		isRunning: false,
		lastRunAt: null,
	}));
	const queue = useState(() => makeTaskQueue(size, { onStateChange: setState }))[0];

	return useMemo(() => ({ queue, state }), [queue, state]);
}

/**
 * Non-hook counterpart of `useTaskQueueWithAtom`, for a queue that has to outlive the component
 * rendering its indicator: enqueued tasks keep running after an unmount, so a queue owned by a
 * `useState` would be replaced on the next mount — losing both the "saved at" state and, worse, the
 * sequencing guarantee against the tasks the previous queue still has in flight. Create one of these
 * at module scope instead, so the same queue and the same state atom are reused across mounts.
 */
export function makeTaskQueueWithAtom(size?: number): [Atom<TaskQueueState>, TaskQueue] {
	const state$ = atomWithCompare<TaskQueueState>({ isRunning: false, lastRunAt: null });
	const queue = makeTaskQueue(size, { onStateChange: (state) => writeAtom(state$, state) });
	return [state$, queue];
}

/**
 * Same as `useTaskQueue`, but provides a store so that UI updates can be isolated from the rest of the page.
 */
export function useTaskQueueWithAtom(size?: number): [Atom<TaskQueueState>, TaskQueue] {
	const [state$, setState] = useAtomState<TaskQueueState>({
		isRunning: false,
		lastRunAt: null,
	});
	const queue = useState(() => makeTaskQueue(size, { onStateChange: setState }))[0];
	return useMemo(() => [state$, queue], [state$, queue]);
}

export function TaskQueueStatusIndicator({
	taskQueueState$,
	labels,
	onRetry,
	...rest
}: {
	taskQueueState$: Atom<TaskQueueState>;
	labels: {
		idle?: string;
		running: string;
		success: (date: Date) => string;
		error: string;
	};
	/**
	 * What to re-run when the last task failed, offered as a Retry beside the error label. Only the code that
	 * authored the failed task knows whether re-running it is safe, so the queue does not offer one of its own.
	 */
	onRetry?: () => Promise<void>;
} & StylableProps &
	DataAttributesProps): React.JSX.Element {
	return (
		<WithAtom atom$={taskQueueState$}>
			{({ isRunning, lastRunAt }) => (
				<Row alignItems="center" gap={4} {...rest}>
					{isRunning ? (
						<>
							<Icon icon="calulating" size={16} />
							<Text as="div" type="Body/S/Medium" data-qualifier="InstrumentCustomization/Sync/Saving">
								{labels.running}
							</Text>
						</>
					) : lastRunAt ? (
						lastRunAt.isSuccess ? (
							<>
								<Icon icon="Outline" size={16} />
								<Text as="div" type="Body/S/Medium" data-qualifier="InstrumentCustomization/Sync/Saved">
									{labels.success(lastRunAt.timestamp)}
								</Text>
							</>
						) : (
							<>
								<Icon icon="Alert" size={18} color={themeCSSVars.global_palette_warning_600} />
								<Text as="div" type="Body/S/Medium" data-qualifier="InstrumentCustomization/Sync/Error">
									{labels.error}
								</Text>
								{onRetry && (
									<ActionText
										color={themeCSSVars.global_palette_warning_600}
										onClickAsync={onRetry}
										type="Body/S/Medium"
										data-qualifier="InstrumentCustomization/Sync/Retry"
									>
										retry
									</ActionText>
								)}
							</>
						)
					) : (
						labels.idle && (
							<>
								<Icon icon="Empty" size={16} />
								<Text as="div" type="Body/S/Medium" data-qualifier="InstrumentCustomization/Sync/Error">
									{labels.idle}
								</Text>
							</>
						)
					)}
				</Row>
			)}
		</WithAtom>
	);
}
